import { Controller } from "@hotwired/stimulus";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA",
  "HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

export default class extends Controller {
  static targets = ["hidden", "rows", "quickAddButton", "multiplierNote"];
  static values = { unitCount: { type: Number, default: 1 } };

  connect() {
    this.render(this.parse(this.hiddenTarget.value));
    this.updateQuickAddLabels();
  }

  addRow(e) {
    e.preventDefault();
    this.appendRow("", "");
  }

  quickAdd(e) {
    e.preventDefault();
    const { state, amount: perItem } = e.currentTarget.dataset;
    const amount = this.computeAmount(perItem);
    const pairs = this.currentPairs();
    const existing = pairs.find(p => p.state === state);
    if (existing) {
      existing.amount = amount;
      this.render(pairs);
    } else {
      this.appendRow(state, amount);
    }
    this.sync();
  }

  remove(e) {
    e.preventDefault();
    e.currentTarget.closest("[data-row]").remove();
    this.sync();
  }

  formatAmount(e) {
    const val = parseFloat(e.target.value);
    if (!isNaN(val)) e.target.value = this.formatDepositAmount(val);
    this.sync();
  }

  sync() {
    this.hiddenTarget.value = this.currentPairs()
      .filter(p => p.state && p.amount)
      .map(p => `${p.state}:${p.amount}`)
      .join(",");
  }

  // private

  computeAmount(perItem) {
    return this.formatDepositAmount(parseFloat(perItem) * (this.unitCountValue || 1));
  }

  updateQuickAddLabels() {
    const count = this.unitCountValue || 1;
    this.quickAddButtonTargets.forEach(btn => {
      const { state, amount: perItem } = btn.dataset;
      const total = this.computeAmount(perItem);
      btn.textContent = count === 1
        ? `${state} $${total}`
        : `${state} $${total} (${count}×$${this.formatDepositAmount(parseFloat(perItem))})`;
    });
    if (this.hasMultiplierNoteTarget) {
      this.multiplierNoteTarget.textContent = count === 1
        ? "no unit count set — using per-item rate"
        : `× ${count} individual units`;
    }
  }

  formatDepositAmount(num) {
    const thousandths = Math.round(num * 1000) % 10;
    return thousandths === 0 ? num.toFixed(2) : num.toFixed(3);
  }

  parse(text) {
    if (!text) return [];
    return text.split(",").map(pair => {
      const [state, amount] = pair.trim().split(":");
      return { state: (state || "").toUpperCase(), amount: amount || "" };
    }).filter(p => p.state);
  }

  currentPairs() {
    return Array.from(this.rowsTarget.querySelectorAll("[data-row]")).map(row => ({
      state: row.querySelector("[data-state-input]").value.toUpperCase(),
      amount: row.querySelector("[data-amount-input]").value
    }));
  }

  render(pairs) {
    this.rowsTarget.innerHTML = "";
    [...pairs].sort((a, b) => a.state.localeCompare(b.state)).forEach(({ state, amount }) => this.appendRow(state, amount));
  }

  appendRow(state, amount) {
    const formatted = amount && !isNaN(parseFloat(amount))
      ? this.formatDepositAmount(parseFloat(amount))
      : amount;

    const stateOptions = US_STATES.map(s =>
      `<option value="${s}" ${s === state ? "selected" : ""}>${s}</option>`
    ).join("");

    const row = document.createElement("div");
    row.dataset.row = "";
    row.className = "d-flex align-items-center mb-1 gap-1";
    row.innerHTML = `
      <select data-state-input data-action="change->state-deposits#sync" class="form-control form-control-sm" style="width:80px">
        <option value=""></option>
        ${stateOptions}
      </select>
      <div class="input-group input-group-sm" style="width:110px">
        <div class="input-group-prepend"><span class="input-group-text">$</span></div>
        <input type="text" data-amount-input
               data-action="input->state-deposits#sync blur->state-deposits#formatAmount"
               class="form-control" value="${formatted}" placeholder="0.00">
      </div>
      <button data-action="click->state-deposits#remove" class="btn btn-sm btn-outline-danger px-2">&times;</button>
    `;
    this.rowsTarget.appendChild(row);
    this.sync();
  }
}
